import { useState, useEffect } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import heroBanner1 from '@/assets/hero-banner-1.webp'
import heroBanner2 from '@/assets/hero-banner-2.webp'
import heroBanner3 from '@/assets/hero-banner-3.webp'
import { Button } from '@/components/ui/button'

const banners = [
  {
    id: 1,
    image: heroBanner1,
    imageMobile: heroBanner1,
    mobileObjectPosition: 'center center',
    alt: 'Suas memórias merecem ser tocadas',
    title: 'Suas memórias merecem',
    highlight: 'ser tocadas.',
    description:
      'Nós transformamos seus momentos digitais em uma experiência para toda a vida.',
    cta: 'Descobrir os modelos',
    link: '#produtos',
  },
  {
    id: 2,
    image: heroBanner2,
    imageMobile: heroBanner2,
    mobileObjectPosition: 'center center',
    alt: 'Design que entende a sua história',
    title: 'Design que entende',
    highlight: 'a sua história.',
    description: 'Composições exclusivas, criadas com precisão e refinamento.',
    cta: 'Descobrir os modelos',
    link: '#produtos',
  },
  {
    id: 3,
    image: heroBanner3,
    imageMobile: heroBanner3,
    mobileObjectPosition: 'center center',
    alt: 'O cuidado está nos detalhes',
    title: 'O cuidado está',
    highlight: 'nos detalhes.',
    description:
      'Um acabamento impecável e com design atemporal, criados por quem é apaixonado pelo que faz.',
    cta: 'Descobrir os modelos',
    link: '#produtos',
  },
]

const HeroBanner = () => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [scrollY, setScrollY] = useState(0)

  /* ========================================
     PARALLAX SCROLL EFFECT (Desktop only)
     ======================================== */
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  /* ========================================
     PRELOAD IMAGES
     ======================================== */
  useEffect(() => {
    banners.forEach(banner => {
      const img = new Image()
      img.src = banner.image
    })
  }, [])

  /* ========================================
     AUTO-SLIDE TIMER
     ======================================== */
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % banners.length)
    }, 5000)

    return () => clearInterval(timer)
  }, [])

  const goToSlide = (index: number) => {
    setCurrentSlide(index)
  }

  const goToPrevious = () => {
    setCurrentSlide(prev => (prev - 1 + banners.length) % banners.length)
  }

  const goToNext = () => {
    setCurrentSlide(prev => (prev + 1) % banners.length)
  }

  return (
    <>
      {/* ========================================
          MOBILE LAYOUT (< 768px)
          - Stacked: image on top, text below
          - Image fully visible (no cropping)
          - Height: auto (content-driven)
          - NO overlays on image
          ======================================== */}
      <section
        className="hero-mobile-section block md:hidden"
        style={{
          width: '100%',
          overflow: 'hidden',
        }}
      >
        {banners.map((banner, index) => (
          <div
            key={banner.id}
            style={{ display: index === currentSlide ? 'block' : 'none' }}
          >
            {/* Mobile Banner - Same layout as desktop, scaled down */}
            <div
              style={{
                position: 'relative',
                width: '100%',
                minHeight: '200px',
              }}
            >
              {/* Image - Fully visible, no cropping */}
              <img
                src={banner.imageMobile}
                alt={banner.alt}
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                }}
                fetchPriority={index === 0 ? 'high' : 'auto'}
                loading={index === 0 ? 'eager' : 'lazy'}
              />

              {/* Text content - LEFT aligned, same position as desktop */}
              <div
                style={{
                  position: 'absolute',
                  top: '32%',
                  transform: 'translateY(-50%)',
                  left: '16px',
                  maxWidth: '55%',
                  zIndex: 5,
                }}
              >
                <h2
                  className="font-serif text-foreground"
                  style={{
                    fontSize: '12px',
                    fontWeight: 400,
                    lineHeight: 1.2,
                    margin: 0,
                  }}
                >
                  {banner.title}
                </h2>
                <p
                  className="font-script text-gold"
                  style={{
                    fontSize: '14px',
                    margin: 0,
                  }}
                >
                  {banner.highlight}
                </p>
                <Button
                  size="sm"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground font-medium"
                  style={{
                    marginTop: '5px',
                    padding: '2px 8px',
                    height: 'auto',
                    fontSize: '7px',
                  }}
                  asChild
                >
                  <a href={banner.link}>{banner.cta}</a>
                </Button>
              </div>

              {/* Navigation arrows */}
              <button
                onClick={goToPrevious}
                className="bg-white/25 hover:bg-white/40 text-foreground"
                style={{
                  position: 'absolute',
                  left: '4px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                  padding: '4px',
                  borderRadius: '50%',
                  border: 'none',
                  cursor: 'pointer',
                }}
                aria-label="Slide anterior"
              >
                <ChevronLeft style={{ width: '12px', height: '12px' }} />
              </button>

              <button
                onClick={goToNext}
                className="bg-white/25 hover:bg-white/40 text-foreground"
                style={{
                  position: 'absolute',
                  right: '4px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                  padding: '4px',
                  borderRadius: '50%',
                  border: 'none',
                  cursor: 'pointer',
                }}
                aria-label="Próximo slide"
              >
                <ChevronRight style={{ width: '12px', height: '12px' }} />
              </button>

              {/* Slide indicators - Bottom right */}
              <div
                style={{
                  position: 'absolute',
                  bottom: '8px',
                  right: '12px',
                  display: 'flex',
                  gap: '4px',
                  zIndex: 10,
                }}
              >
                {banners.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => goToSlide(idx)}
                    style={{
                      height: '4px',
                      width: idx === currentSlide ? '12px' : '4px',
                      borderRadius: '2px',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      backgroundColor:
                        idx === currentSlide
                          ? 'rgba(0,0,0,0.7)'
                          : 'rgba(0,0,0,0.3)',
                    }}
                    aria-label={`Ir para slide ${idx + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* ========================================
          DESKTOP LAYOUT (>= 768px)
          - Original overlay layout preserved
          - Text positioned on left side
          - Parallax effect enabled
          - Height: 500px fixed
          ======================================== */}
      <section className="hero-desktop-section hidden md:block relative h-[500px] overflow-hidden">
        {banners.map((banner, index) => {
          const parallaxOffset = scrollY * 0.5

          return (
            <div
              key={banner.id}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              {/* Desktop Image with Parallax */}
              <div
                className="absolute inset-0 w-full h-full"
                style={{
                  transform: `translateY(${parallaxOffset}px)`,
                  transition: 'transform 0.1s ease-out',
                }}
              >
                <img
                  src={banner.image}
                  alt={banner.alt}
                  className="w-full h-full object-cover scale-110"
                  fetchPriority={index === 0 ? 'high' : 'auto'}
                  loading={index === 0 ? 'eager' : 'lazy'}
                />
              </div>

              {/* Desktop Text Overlay - Left aligned */}
              <div
                className="absolute z-20 top-1/2 -translate-y-1/2 left-16 lg:left-24 max-w-xl"
                style={{
                  transform: `translateY(calc(-50% + ${-parallaxOffset * 0.3}px))`,
                  transition: 'transform 0.1s ease-out',
                }}
              >
                <div className="mb-4">
                  <h2 className="font-serif text-4xl lg:text-5xl font-normal text-foreground leading-tight">
                    {banner.title}
                  </h2>
                  <p className="font-script text-4xl lg:text-5xl text-gold">
                    {banner.highlight}
                  </p>
                  <p className="font-sans text-lg text-muted-foreground mt-3">
                    {banner.description}
                  </p>
                </div>
                <Button
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-lg hover:shadow-xl transition-all"
                  asChild
                >
                  <a href={banner.link}>{banner.cta}</a>
                </Button>
              </div>
            </div>
          )
        })}

        {/* Desktop Navigation Arrows */}
        <button
          onClick={goToPrevious}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-30 bg-white/30 hover:bg-white/50 text-white p-2 rounded-full transition-all backdrop-blur-sm"
          aria-label="Slide anterior"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>

        <button
          onClick={goToNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-30 bg-white/30 hover:bg-white/50 text-white p-2 rounded-full transition-all backdrop-blur-sm"
          aria-label="Próximo slide"
        >
          <ChevronRight className="h-6 w-6" />
        </button>

        {/* Desktop Slide Indicators */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex space-x-2">
          {banners.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-2 rounded-full transition-all ${
                index === currentSlide ? 'w-8 bg-white' : 'w-2 bg-white/50'
              }`}
              aria-label={`Ir para slide ${index + 1}`}
            />
          ))}
        </div>
      </section>
    </>
  )
}

export default HeroBanner
